import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GetSupportedAispInstitutions } from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { SupportedInstitution } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { ApiActions, ApiLoadingState } from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { waitForFlutterWebView } from '@app/finxone-web-frontend/app/lib/utils/flutter.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  baseCssConfigStyle,
  BaseWidgetProperties,
  commonContentStyleList,
  commonTextContentStyleList,
  ConfigJsonType,
  ProviderClass,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { BaseComponent } from '../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-banks-listing-widget',
  templateUrl: './banks-listing-widget.component.html',
  styleUrls: ['./banks-listing-widget.component.scss'],
})
export class BanksListingWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Select(SavingsAccountState.getSupportedInstitutions)
  private supportedInstitutions$!: Observable<SupportedInstitution[]>;
  @Select(ApiLoadingState.getApiState(ApiActions.supportedAispInstitutionsLoading))
  private getSupportedAispInstitutionsLoadingState$: Observable<{ isLoading: boolean }>;
  public widgetConfig: BaseWidgetProperties;
  public navigateToUrl = '/nominated-account-status';
  public textContentStyle: baseCssConfigStyle;
  public searchIconStyle: baseCssConfigStyle;
  public searchInputStyle: baseCssConfigStyle;
  public searchInputInactiveStyle: baseCssConfigStyle;
  public searchInputBaseStyle: baseCssConfigStyle;
  public supportedInstitutions: SupportedInstitution[];
  public widgetName = WidgetNames.BANK_LISTING_WIDGET;
  public config: ConfigJsonType;
  private baseUrl: string;
  private flutterWebViewInstance: any;
  public isApiInProgress = false;

  constructor(
    public dataIn: ProviderClass<any>,
    public store: Store,
    public configService: ConfigService,
    public savingsAccountService: SavingsAccountService,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.dataIn?.attri) {
      this.widgetConfig = this.dataIn.attri.widgetProperties;
    }
    this.getWidgetCssConfig();
    this.supportedInstitutions$
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          if (!response?.length) {
            const country = this.store.selectSnapshot(ProfileState.getProfile)?.country;
            this.store.dispatch(new GetSupportedAispInstitutions(country));
          }
          this.supportedInstitutions = response;
        }),
      )
      .subscribe();

    this.configService
      .getApi()
      .pipe(
        take(1),
        tap((baseUrl) => {
          this.baseUrl = baseUrl;
        }),
      )
      .subscribe();

    waitForFlutterWebView().then((flutterWebView) => {
      this.flutterWebViewInstance = flutterWebView;
    });
    this.setupLoadingStateSubscription();
  }

  private setupLoadingStateSubscription(): void {
    this.getSupportedAispInstitutionsLoadingState$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isApiInProgress = result.isLoading;
    });
  }

  public getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonContentStyleList,
    );

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonTextContentStyleList,
    );

    const { iconStyle, baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.widgetConfig,
    );

    this.searchIconStyle = iconStyle;
    this.searchInputStyle = baseStyle;
    this.searchInputBaseStyle = activeStyle;
    this.searchInputInactiveStyle = inactiveStyle;
  }

  public handleSelection(supportedInstitution: SupportedInstitution) {
    // determine the env to set the supportedInstitution.id to sandbox bank for dev and projects env
    const isNotProdEnv = this.baseUrl.includes('development') || this.baseUrl.includes('projects');

    const overrideGoCardlessDevModeForAISP =
      this.configService.config.overrideGoCardlessDevModeForAISP || false;
    this.savingsAccountService
      .connectNominatedAccount({
        // use sandbox bank for dev and projects env
        institutionId:
          isNotProdEnv && !overrideGoCardlessDevModeForAISP
            ? 'SANDBOXFINANCE_SFIN0000'
            : supportedInstitution.id,
        redirectUrl: this.baseUrl + this.navigateToUrl,
      })
      .pipe(
        tap((res) => {
          if (res?.requestId) {
            sessionStorage.setItem('requestId', res.requestId);
            if (!this.flutterWebViewInstance || this.flutterWebViewInstance === null)
              this.flutterWebViewInstance = (window as any).flutter_inappwebview;
            // call the flutter handler before entering external flow
            this.flutterWebViewInstance?.callHandler('InitiateExternalUrl', []);
            //redirect to external link
            window.location.href = res.link;
          }
        }),
      )
      .subscribe();
  }
}
